import { useEffect, useMemo, useRef, useState } from "react";
import { bulkUploadEmployees } from "./EmployeeApi";
import { EMPLOYEE_FIELDS, getBulkTargetFields } from "./employeeFieldSchema";
import { autoMapColumns, coerceEmployeeRecord } from "./employeeValidation";
import BulkEmployeeFinalReview from "./components/BulkEmployeeFinalReview";
import BulkEmployeeRequirementsStep, {
  validateBulkEmployeeRows,
} from "./components/BulkEmployeeRequirementsStep";
import BulkUploadStepBar from "./components/BulkUploadStepBar";
import {
  buildErrorReportCsvBlob,
  buildSampleCsvBlob,
  buildSampleXlsxBlob,
  downloadBlob,
  parseCsv,
  parseXlsx,
  toCsv,
} from "./employeeFileUtils";
import { DEFAULT_EMPLOYEE_ROLE } from "./employeeRoleConstants";
import { DEFAULT_EMPLOYEE_TYPE } from "./employeeTypeConstants";
import BrixigoPageLoader from "../../components/common/BrixigoPageLoader";

function pill(kind) {
  if (kind === "ok") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (kind === "warn") return "bg-amber-50 text-amber-800 border-amber-200";
  if (kind === "bad") return "bg-red-50 text-red-700 border-red-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

const dropCls =
  "border-2 border-dashed rounded-2xl p-6 bg-white transition flex flex-col items-center justify-center text-center";

function normaliseRowValue(v) {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function MappingSelect({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export default function EmployeeBulkUploadUnified({ lookups, onComplete }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parseState, setParseState] = useState({ headers: [], rows: [] });
  const [mapping, setMapping] = useState({});
  const [stage, setStage] = useState("select");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [draftRows, setDraftRows] = useState([]);

  const targetFields = useMemo(() => getBulkTargetFields(), []);

  const mappingOptions = useMemo(() => {
    const opts = [];
    for (const f of targetFields) {
      opts.push({ value: f.key, label: `${f.label} (${f.key})` });
      if (f.bulk?.secondaryNameKey) {
        opts.push({
          value: f.bulk.secondaryNameKey,
          label: `${f.label} Name (${f.bulk.secondaryNameKey})`,
        });
      }
    }
    return opts;
  }, [targetFields]);

  const reverseMap = useMemo(() => {
    // sourceHeader -> targetKey
    const out = {};
    for (const [k, v] of Object.entries(mapping || {})) {
      if (v) out[v] = k;
    }
    return out;
  }, [mapping]);

  useEffect(() => {
    if (!parseState.headers.length) return;
    setMapping((prev) => ({ ...autoMapColumns(parseState.headers), ...prev }));
  }, [parseState.headers]);

  // File mapping: personal + compensation columns only. Org IDs & role are completed per employee later.
  const requiredTargets = useMemo(() => {
    const skip = new Set(["department_id", "designation_id", "location_id", "role"]);
    return new Set(
      EMPLOYEE_FIELDS.filter((f) => f.bulk?.required && !skip.has(f.key)).map((f) => f.key)
    );
  }, []);

  const parseFile = async (f) => {
    setResult(null);
    setDraftRows([]);
    setMapping({});
    setParseState({ headers: [], rows: [] });

    const name = String(f?.name || "").toLowerCase();
    let parsed;
    if (name.endsWith(".csv")) parsed = await parseCsv(f);
    else if (name.endsWith(".xlsx")) parsed = await parseXlsx(f);
    else throw new Error("Only CSV or XLSX is supported");

    setParseState(parsed);
    setStage("map");
  };

  const onPickFile = (f) => {
    if (!f) return;
    setFile(f);
    parseFile(f).catch((e) => alert(e.message || "Failed to parse file"));
  };

  const buildCanonical = () => {
    const rows = [];

    for (let i = 0; i < (parseState.rows || []).length; i++) {
      const raw = parseState.rows[i] || {};
      const record = {};

      for (const [targetKey, sourceHeader] of Object.entries(mapping || {})) {
        if (!sourceHeader) continue;
        record[targetKey] = normaliseRowValue(raw?.[sourceHeader]);
      }

      if (!record.role) record.role = DEFAULT_EMPLOYEE_ROLE;
      if (!record.employee_type) record.employee_type = DEFAULT_EMPLOYEE_TYPE;

      rows.push(coerceEmployeeRecord(record, { lookups }));
    }

    if (!rows.length) {
      alert("No employee rows found in the file.");
      return;
    }

    setDraftRows(rows.map((r) => ({ ...r })));
    setStage("preview");
  };

  const mappingCompleteness = useMemo(() => {
    const mappedTargets = new Set(Object.keys(mapping || {}).filter((k) => mapping[k]));
    const missing = Array.from(requiredTargets).filter((t) => !mappedTargets.has(t));
    return { missing, ok: missing.length === 0 };
  }, [mapping, requiredTargets]);

  const previewValidation = useMemo(
    () => validateBulkEmployeeRows(draftRows, lookups),
    [draftRows, lookups]
  );

  const handleUpload = async () => {
    const check = validateBulkEmployeeRows(draftRows, lookups);
    if (!check.allValid) {
      alert("Some employees are still incomplete. Go back and fill all required fields.");
      setStage("requirements");
      return;
    }

    const base = draftRows.map((r) => coerceEmployeeRecord(r, { lookups }));
    const csv = toCsv(base, Object.keys(base?.[0] || {}));
    const cleanFile = new File([csv], "employees_canonical.csv", { type: "text/csv" });

    setUploading(true);
    setStage("uploading");
    try {
      const res = await bulkUploadEmployees(cleanFile);
      setResult(res);
      setStage("result");
    } catch (e) {
      console.error(e);
      alert("Failed to create employees. Please try again.");
      setStage("review");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* ── Upload card ── */}
      <div className="emp-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Bulk Upload</h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Upload CSV or XLSX with the same fields as Add Employee — including banking and statutory.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={() => downloadBlob(buildSampleCsvBlob(), "employees_sample.csv")}
              className="emp-upload-btn-csv"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Sample CSV
            </button>
            <button
              type="button"
              onClick={() => downloadBlob(buildSampleXlsxBlob(), "employees_sample.xlsx")}
              className="emp-upload-btn-xlsx"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              Sample XLSX
            </button>
          </div>
        </div>

        {stage === "select" && (
          <div
            className="emp-drop-zone mt-5"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              onPickFile(f);
            }}
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0])}
            />
            <div className="emp-drop-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
              </svg>
            </div>
            <h4 className="text-lg font-bold text-slate-800">Drag &amp; drop your CSV/XLSX</h4>
            <p className="text-sm text-slate-500 mt-1.5">
              Or{" "}
              <span className="text-indigo-600 font-bold cursor-pointer hover:underline">browse files</span>
            </p>
            <div className="mt-4 flex items-center gap-3">
              <span className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold">.CSV</span>
              <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">.XLSX</span>
              <span className="text-xs text-slate-400">· Recommended: under 5,000 rows</span>
            </div>
          </div>
        )}
      </div>

      <BulkUploadStepBar stage={stage} />

      {/* ── Map columns ── */}
      {stage === "map" && (
        <div className="emp-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center text-indigo-600">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">Map Columns</h4>
                <p className="text-sm text-slate-500">We auto-map when headers match — verify required fields below.</p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <span className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${mappingCompleteness.ok ? pill("ok") : pill("warn")}`}>
                {mappingCompleteness.ok ? "✓ All required mapped" : `${mappingCompleteness.missing.length} required missing`}
              </span>
              <button
                type="button"
                onClick={() => buildCanonical()}
                disabled={!mappingCompleteness.ok}
                className="emp-upload-btn-primary"
              >
                Continue to preview →
              </button>
            </div>
          </div>

          {!mappingCompleteness.ok && (
            <div className="mt-3 p-3.5 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800 flex items-start gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              Missing required: <b className="ml-1">{mappingCompleteness.missing.join(", ")}</b>
            </div>
          )}

          <div className="mt-4 overflow-auto border border-slate-200 rounded-xl shadow-sm">
            <table className="min-w-[900px] w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                  <th className="text-left px-4 py-3 font-bold">Source column</th>
                  <th className="text-left px-4 py-3 font-bold">Map to field</th>
                  <th className="text-left px-4 py-3 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {(parseState.headers || []).map((h) => {
                  const selected = reverseMap[h] || "";
                  const isRequiredMapped = requiredTargets.has(selected);
                  return (
                    <tr key={h} className="odd:bg-white even:bg-indigo-50/30 hover:bg-indigo-50/60 transition-colors">
                      <td className="px-4 py-2.5 border-b border-slate-100 font-medium text-slate-800">
                        <code className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-700 text-xs">{h}</code>
                      </td>
                      <td className="px-3 py-2 border-b border-slate-100">
                        <MappingSelect
                          value={selected}
                          options={mappingOptions}
                          placeholder="(Ignore this column)"
                          onChange={(targetKey) => {
                            setMapping((prev) => {
                              const next = { ...prev };
                              // clear any other mapping pointing to this header
                              Object.keys(next).forEach((k) => {
                                if (next[k] === h) delete next[k];
                              });
                              if (targetKey) next[targetKey] = h;
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 border-b border-slate-100">
                        {selected ? (
                          <span className={`text-xs px-2 py-1 rounded-full border ${pill(isRequiredMapped ? "ok" : "neutral")}`}>
                            {isRequiredMapped ? "Required mapped" : "Mapped"}
                          </span>
                        ) : (
                          <span className={`text-xs px-2 py-1 rounded-full border ${pill("warn")}`}>Ignored</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                setStage("select");
                setFile(null);
                setParseState({ headers: [], rows: [] });
                setMapping({});
              }}
              className="emp-upload-btn-secondary"
            >
              ← Choose another file
            </button>
            <button
              type="button"
              onClick={() => setMapping((prev) => ({ ...prev, ...autoMapColumns(parseState.headers) }))}
              className="emp-upload-btn-secondary"
            >
              Auto-map again
            </button>
          </div>
        </div>
      )}

      {/* ── Import preview ── */}
      {stage === "preview" && (
        <div className="emp-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center text-blue-600">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">Import Preview</h4>
                <p className="text-sm text-slate-500">
                  <span className="font-semibold text-indigo-600">{draftRows.length}</span> employee{draftRows.length === 1 ? "" : "s"} parsed — nothing saved yet.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setStage("requirements")}
              className="emp-upload-btn-primary"
            >
              Complete requirements →
            </button>
          </div>

          <div className="mt-3.5 p-3.5 rounded-xl border border-indigo-200 bg-indigo-50/60 text-sm text-indigo-900 flex items-start gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5 text-indigo-500"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span>Department, designation, location, and role must be set for <b>every</b> employee on the next screen.
              {previewValidation.completed < previewValidation.total
                ? <> Currently <b>{previewValidation.total - previewValidation.completed}</b> row(s) need attention.</>
                : null}
            </span>
          </div>

          <div className="mt-4 overflow-auto border border-slate-200 rounded-xl shadow-sm">
            <table className="min-w-[1000px] w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                  <th className="text-left px-4 py-3 font-bold">#</th>
                  <th className="text-left px-4 py-3 font-bold">Name</th>
                  <th className="text-left px-4 py-3 font-bold">Email</th>
                  <th className="text-left px-4 py-3 font-bold">Type</th>
                  <th className="text-left px-4 py-3 font-bold">DOB</th>
                  <th className="text-left px-4 py-3 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {draftRows.slice(0, 50).map((r, idx) => {
                  const err = previewValidation.rowErrors.find((e) => e.rowIndex === idx);
                  const name = [r.first_name, r.last_name].filter(Boolean).join(" ");
                  return (
                    <tr key={idx} className="odd:bg-white even:bg-indigo-50/30 hover:bg-indigo-50/60 transition-colors">
                      <td className="px-4 py-2.5 border-b border-slate-100 text-slate-500 text-xs font-semibold">{idx + 1}</td>
                      <td className="px-4 py-2.5 border-b border-slate-100 font-semibold text-slate-800">{name || "—"}</td>
                      <td className="px-4 py-2.5 border-b border-slate-100 text-slate-600">{r.work_email || r.email || "—"}</td>
                      <td className="px-4 py-2.5 border-b border-slate-100 text-slate-600">{r.employee_type || "—"}</td>
                      <td className="px-4 py-2.5 border-b border-slate-100 text-slate-600">{r.date_of_birth || "—"}</td>
                      <td className="px-4 py-2.5 border-b border-slate-100">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${err ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                          {err ? "⚠ Needs completion" : "✓ Imported"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStage("map")}
              className="emp-upload-btn-secondary"
            >
              ← Back to mapping
            </button>
            <p className="text-xs text-slate-400">Showing first 50 rows.</p>
          </div>
        </div>
      )}

      {stage === "requirements" && (
        <BulkEmployeeRequirementsStep
          rows={draftRows}
          lookups={lookups}
          onRowsChange={setDraftRows}
          onBack={() => setStage("preview")}
          onContinue={() => setStage("review")}
        />
      )}

      {stage === "review" && (
        <BulkEmployeeFinalReview
          rows={draftRows}
          lookups={lookups}
          uploading={uploading}
          onBack={() => setStage("requirements")}
          onConfirm={handleUpload}
        />
      )}

      {/* ── Uploading ── */}
      {stage === "uploading" && (
        <div className="emp-card flex flex-col items-center py-10 gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200 animate-pulse">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
            </svg>
          </div>
          <div className="text-center">
            <p className="font-bold text-slate-800 text-base">Creating employees…</p>
            <p className="text-sm text-slate-500 mt-1">Please wait, this may take a moment.</p>
          </div>
        </div>
      )}

      {/* ── Result ── */}
      {stage === "result" && result && (
        <div className="emp-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg ${result.failed === 0 ? "bg-gradient-to-br from-emerald-400 to-teal-500 shadow-emerald-200" : "bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-200"}`}>
                {result.failed === 0 ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                )}
              </div>
              <div>
                <h4 className="text-base font-bold text-slate-900">Upload Complete</h4>
                <p className="text-sm text-slate-500">
                  {result.failed === 0 ? "All employees created successfully." : "Some rows had issues — download the report below."}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              {result?.failed > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const blob = buildErrorReportCsvBlob({
                      canonicalRows: draftRows,
                      rowErrors: (result.errors || []).map((e) => ({
                        row: e.row,
                        rowIndex: (e.row || 1) - 1,
                        error: e.error,
                      })),
                    });
                    downloadBlob(blob, "employees_upload_errors.csv");
                  }}
                  className="emp-upload-btn-secondary"
                >
                  Download Error Report
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setStage("select");
                  setFile(null);
                  setParseState({ headers: [], rows: [] });
                  setMapping({});
                  setResult(null);
                  setDraftRows([]);
                }}
                className="emp-upload-btn-primary"
              >
                Upload another file
              </button>
              {onComplete && (
                <button
                  type="button"
                  onClick={onComplete}
                  className="emp-upload-btn-secondary"
                >
                  View employee list →
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-4 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/60">
              <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Total Rows</p>
              <p className="text-3xl font-black text-slate-800 mt-1">{result.total_rows}</p>
            </div>
            <div className="p-4 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
              <p className="text-xs text-emerald-600 font-bold uppercase tracking-wider">✓ Inserted</p>
              <p className="text-3xl font-black text-emerald-700 mt-1">{result.inserted}</p>
            </div>
            <div className="p-4 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
              <p className="text-xs text-amber-600 font-bold uppercase tracking-wider">⚠ Failed</p>
              <p className="text-3xl font-black text-amber-700 mt-1">{result.failed}</p>
            </div>
          </div>

          {result?.failed > 0 && (
            <div className="mt-4 overflow-auto max-h-80 border border-slate-200 rounded-xl shadow-sm">
              <table className="min-w-[800px] w-full text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-700 to-slate-800">
                    <th className="text-left px-4 py-3 font-bold text-white w-20">Row</th>
                    <th className="text-left px-4 py-3 font-bold text-white">Issue</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.errors || []).slice(0, 200).map((e, idx) => (
                    <tr key={idx} className="odd:bg-white even:bg-slate-50/40 hover:bg-amber-50/40 transition-colors">
                      <td className="px-4 py-2.5 border-b border-slate-100 font-bold text-slate-700">{e.row}</td>
                      <td className="px-4 py-2.5 border-b border-slate-100 text-amber-800">{e.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

